/**
 * Unified Camera Hook
 * Consolidates camera capture logic for both mobile and desktop
 * Fixes black frame issue with proper readyState checking
 * Implements resource management and memory optimization
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { toast } from 'sonner'
// blobToBase64 no longer needed — we strip the data URL prefix directly
import { WEBSOCKET_CONFIG, RATE_LIMITS, MEDIA_CAPTURE_CONFIG } from '../../config/constants.js'
import { logger } from '../../lib/logger-client.js'
import type { LiveMediaFrame } from '../../live/liveTypes.js'
import {
  captureVideoFrame,
  validateFrameQuality,
  createMetrics,
  recordCaptureTime,
  getMetricsSnapshot,
  stopStreamTracks,
  registerVisibilityCleanup,
  waitForVideoReady,
  maybeAnalyzeAndBroadcast,
  type CaptureMetrics,
} from './capture-utils.js'
import { CaptureLifecycle } from './capture-lifecycle.js'
import { cameraFailureMessage, mediaLocaleFrom } from '../../lib/media-capture-message.js'

export interface UseCameraOptions {
  onCapture?: (blob: Blob, imageData?: string) => void
  onAnalysis?: (analysis: string, imageData: string, capturedAt: number) => void
  captureInterval?: number
  enableAutoCapture?: boolean
  maxDimension?: number
  quality?: number
  sessionId?: string
  voiceConnectionId?: string
  requireVoiceSession?: boolean
  sendRealtimeInput?: (chunks: Array<{ mimeType: string; data: string }>) => void
  sendVideoFrame?: (frame: LiveMediaFrame) => boolean | void
  sendContextUpdate?: (update: {
    sessionId?: string | null | undefined
    modality: 'screen' | 'webcam'
    analysis: string
    imageData?: string | undefined
    capturedAt?: number | undefined
    metadata?: Record<string, unknown> | undefined
  }) => void
  analyzeVisualDelta?: (
    currentAnalysis: string,
    previousAnalysis?: string
  ) => {
    significant: boolean
    score: number
  }
  getLiveTransportState?:
    | (() => {
        isConnected: boolean
        isSessionReady: boolean
        bufferedAmount: number
      })
    | undefined
}

export interface CameraCapture {
  blob: Blob
  imageData?: string
  timestamp: number
  metadata?: {
    width: number
    height: number
    deviceId?: string
  }
}

// CameraMetrics is now CaptureMetrics from capture-utils
type CameraMetrics = CaptureMetrics

type ExtendedCaptureMetrics = CameraMetrics & { lastAnalysis?: string }
type CameraStartFailure = { key: string; at: number }

export function useCamera(options: UseCameraOptions = {}) {
  const {
    onCapture,
    onAnalysis,
    captureInterval = 12000,
    enableAutoCapture = false,
    maxDimension = 1280,
    quality = 0.7,
    sessionId,
    voiceConnectionId,
    requireVoiceSession = false,
    sendRealtimeInput,
    sendVideoFrame,
    sendContextUpdate,
    analyzeVisualDelta,
    getLiveTransportState,
  } = options

  const [isActive, setIsActive] = useState(false)
  const [hasVerifiedFrame, setHasVerifiedFrame] = useState(false)
  const [isInitializing, setIsInitializing] = useState(false)
  /** Synchronous guard: two starts in one tick must open one permission prompt. */
  const startingRef = useRef(false)
  const [stream, setStream] = useState<MediaStream | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [availableDevices, setAvailableDevices] = useState<MediaDeviceInfo[]>([])
  const [currentDeviceId, setCurrentDeviceId] = useState<string | undefined>()
  const streamSessionLockRef = useRef<string | null>(null)
  const facingModeRef = useRef<'user' | 'environment'>('user')
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user')
  const startFailureRef = useRef<CameraStartFailure | null>(null)

  const videoRef = useRef<HTMLVideoElement | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const captureLifecycleRef = useRef<CaptureLifecycle | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const metricsRef = useRef<ExtendedCaptureMetrics>(createMetrics())
  const lastAnalysisAtRef = useRef(0)
  const ANALYSIS_INTERVAL_MS = 4000

  // validateFrameQuality is imported from capture-utils

  const frameQueueRef = useRef<Array<{ mimeType: string; data: string }>>([])
  const queueProcessTimerRef = useRef<number | null>(null)
  const currentQualityRef = useRef<number>(quality)
  const isStreamingActiveRef = useRef<boolean>(false)
  const lastBackpressureWarnAtRef = useRef<number>(0)
  const hasSentFirstFrameRef = useRef(false)

  const sendRealtimeInputRef = useRef(sendRealtimeInput)
  const sendVideoFrameRef = useRef(sendVideoFrame)
  const captureFrameRef = useRef<(() => Promise<CameraCapture | null>) | null>(null)
  const hasLiveFrameTransport = Boolean(sendVideoFrame || sendRealtimeInput)
  // Frame buffer for quality selection (last 3-5 frames)
  const frameBufferRef = useRef<Array<{ imageData: string; quality: number; timestamp: number }>>(
    []
  )
  const MAX_BUFFER_SIZE = 5

  useEffect(() => {
    sendRealtimeInputRef.current = sendRealtimeInput
  }, [sendRealtimeInput])

  useEffect(() => {
    sendVideoFrameRef.current = sendVideoFrame
  }, [sendVideoFrame])

  useEffect(() => {
    if (typeof document !== 'undefined' && !canvasRef.current) {
      canvasRef.current = document.createElement('canvas')
    }
    return () => {
      canvasRef.current = null
    }
  }, [])

  const stopCamera = useCallback(() => {
    captureLifecycleRef.current?.stop()

    if (queueProcessTimerRef.current) {
      clearInterval(queueProcessTimerRef.current)
      queueProcessTimerRef.current = null
    }

    frameQueueRef.current = []
    currentQualityRef.current = quality
    isStreamingActiveRef.current = false
    startFailureRef.current = null
    hasSentFirstFrameRef.current = false

    if (streamRef.current) {
      stopStreamTracks(streamRef.current)
      streamRef.current = null
    }

    if (videoRef.current) {
      try {
        videoRef.current.pause?.()
      } catch (pauseError) {
        logger.debug('📷 [useCamera] Unable to pause preview element', { error: pauseError })
      }
      videoRef.current.srcObject = null
    }
    videoRef.current = null
    streamSessionLockRef.current = null

    setStream(null)
    setIsActive(false)
    setHasVerifiedFrame(false)
    setError(null)
    setCurrentDeviceId(undefined)
  }, [quality])

  // Edge Case 2: "Silent Tab Crash" — cleanup media on tab hide/close (iOS memory)
  useEffect(() => {
    return registerVisibilityCleanup({
      label: '📷 [useCamera]',
      getStream: () => streamRef.current,
      clearStream: () => {
        streamRef.current = null
      },
      onStop: stopCamera,
    })
  }, [stopCamera])

  const enumerateDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      const videoDevices = devices.filter(device => device.kind === 'videoinput')
      setAvailableDevices(videoDevices)
      return videoDevices
    } catch (err) {
      logger.error(
        'Failed to enumerate devices:',
        err instanceof Error ? err : new Error(String(err))
      )
      return []
    }
  }, [])

  const startCamera = useCallback(
    async (deviceId?: string, facingOverride?: 'user' | 'environment') => {
      if (isInitializing || startingRef.current) {
        logger.debug('Camera initialization already in progress')
        return
      }

      const desiredFacing = facingOverride || facingModeRef.current || 'user'
      const startKey = deviceId ? `device:${deviceId}` : `facing:${desiredFacing}`
      const recentFailure = startFailureRef.current
      if (
        recentFailure?.key === startKey &&
        Date.now() - recentFailure.at < MEDIA_CAPTURE_CONFIG.START_FAILURE_RETRY_COOLDOWN_MS
      ) {
        logger.debug('📷 [useCamera] Suppressing repeated camera start after recent failure', {
          startKey,
        })
        return null
      }

      startingRef.current = true
      setIsInitializing(true)
      setError(null)

      try {
        // Reuse existing stream if it has active tracks and no device/facing change requested
        const existingStream = streamRef.current
        if (
          existingStream &&
          !deviceId &&
          !facingOverride &&
          existingStream.getVideoTracks().some(t => t.readyState === 'live' && t.enabled)
        ) {
          logger.debug('📷 [useCamera] Reusing existing active stream (avoids media renegotiation)', {
            streamId: existingStream.id,
            tracks: existingStream.getVideoTracks().length,
          })
          setStream(existingStream)
          setIsActive(true)
          setIsInitializing(false)
          return existingStream
        }

        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => {
            track.stop()
          })
          streamRef.current = null
        }

        const constraints: MediaStreamConstraints = {
          video: deviceId
            ? { deviceId: { exact: deviceId } }
            : {
                facingMode: desiredFacing,
                width: { ideal: 1280 },
                height: { ideal: 720 },
              },
          audio: false,
        }

        logger.debug('📷 [useCamera] Requesting getUserMedia with constraints:', constraints)

        if (typeof navigator === 'undefined' || !navigator.mediaDevices) {
          throw new Error('Camera access is not supported in this browser.')
        }
        if (!navigator.mediaDevices.getUserMedia) {
          throw new Error('Camera API is not available. Please use a modern browser.')
        }
        if (!window.isSecureContext) {
          throw new Error(
            'Camera access requires a secure connection (HTTPS). Please access this page via HTTPS.'
          )
        }

        const timeoutSec = Math.round(MEDIA_CAPTURE_CONFIG.CAMERA_START_TIMEOUT_MS / 1000)
        const maxAttempts = MEDIA_CAPTURE_CONFIG.CAMERA_START_MAX_ATTEMPTS ?? 1

        let mediaStream: MediaStream | null = null
        let lastError: Error | null = null

        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
          let timeoutId: ReturnType<typeof setTimeout> | undefined
          let mediaTimedOut = false
          const mediaPromise = navigator.mediaDevices.getUserMedia(constraints)
          mediaPromise
            .then(lateStream => {
              if (mediaTimedOut && streamRef.current !== lateStream) {
                stopStreamTracks(lateStream)
                logger.warn('📷 [useCamera] Released camera stream that arrived after timeout')
              }
            })
            .catch(() => undefined)

          try {
            mediaStream = await Promise.race([
              mediaPromise,
              new Promise<never>((_, reject) => {
                timeoutId = setTimeout(() => {
                  mediaTimedOut = true
                  reject(
                    new Error(
                      `Camera access timed out after ${timeoutSec}s. Please check your browser permissions and try again.`
                    )
                  )
                }, MEDIA_CAPTURE_CONFIG.CAMERA_START_TIMEOUT_MS)
              }),
            ])
            lastError = null
            break
          } catch (err) {
            lastError = err instanceof Error ? err : new Error(String(err))
            // A timeout means the permission prompt is still open, and a denial is the
            // visitor's answer. Asking again opens a second prompt.
            if (mediaTimedOut || ['NotAllowedError', 'SecurityError'].includes(lastError.name)) break
            if (attempt < maxAttempts) {
              logger.info('📷 [useCamera] Camera start failed, retrying', {
                attempt,
                maxAttempts,
                error: lastError.message,
              })
              await new Promise(resolve => setTimeout(resolve, 500))
            }
          } finally {
            if (timeoutId) clearTimeout(timeoutId)
          }
        }

        if (!mediaStream) {
          throw lastError ?? new Error('Camera access failed')
        }

        logger.debug('📷 [useCamera] getUserMedia success, stream tracks:', {
          tracks: mediaStream.getTracks().length,
        })

        const videoTrack = mediaStream.getVideoTracks()[0]
        if (!videoTrack) {
          throw new Error('No video track available')
        }
        const settings = videoTrack.getSettings()
        setCurrentDeviceId(settings.deviceId)
        try {
          interface MediaStreamTrackWithLabel {
            label?: string
            getSettings(): MediaTrackSettings
          }
          interface MediaTrackSettingsWithLabel extends MediaTrackSettings {
            label?: string
          }
          const settings = videoTrack.getSettings() as MediaTrackSettingsWithLabel
          const trackWithLabel = videoTrack as MediaStreamTrackWithLabel
          const label = settings?.label || trackWithLabel.label || ''
          if (/back|rear|environment/i.test(label)) {
            facingModeRef.current = 'environment'
            setFacingMode('environment')
          } else if (/front|user|face/i.test(label)) {
            facingModeRef.current = 'user'
            setFacingMode('user')
          }
        } catch (labelError) {
          logger.debug('📷 [useCamera] Unable to infer facing mode from label', {
            error: labelError,
          })
        }

        const onTrackEnded = () => {
          logger.debug('Camera track ended')
          stopCamera()
        }
        videoTrack.addEventListener('ended', onTrackEnded)
        // Store cleanup on the track so stopCamera can remove it if needed
        ;(videoTrack as MediaStreamTrack & { _fbcEndedHandler?: () => void })._fbcEndedHandler =
          onTrackEnded

        streamRef.current = mediaStream
        streamSessionLockRef.current = mediaStream.id
        startFailureRef.current = null
        setStream(mediaStream)
        setIsActive(true)
        setIsInitializing(false)
        logger.info('📷 [useCamera] Camera started successfully, isActive=true')

        if (!videoRef.current) {
          // No video element attached yet (WebcamPreview hasn't mounted) — create internal one.
          // Do NOT assign srcObject here; the stream sync useEffect is the canonical path.
          try {
            const internalVideo = document.createElement('video')
            internalVideo.muted = true
            internalVideo.playsInline = true
            videoRef.current = internalVideo
            logger.debug('📷 [useCamera] Created internal video element (stream will sync via effect)')
          } catch (internalErr) {
            logger.debug('📷 [useCamera] Unable to create internal video element', {
              error: internalErr,
            })
          }
        }
        // NOTE: srcObject assignment is handled exclusively by the stream sync useEffect below.
        // This prevents 4 competing assignment paths from causing flickering/races.

        await enumerateDevices()

        return mediaStream
      } catch (err) {
        const locale = mediaLocaleFrom(
          typeof document !== 'undefined' ? document.documentElement.lang : undefined,
        )
        const message = cameraFailureMessage(err, locale)

        if (streamRef.current) {
          stopStreamTracks(streamRef.current)
          streamRef.current = null
        }
        if (videoRef.current) {
          videoRef.current.srcObject = null
        }
        streamSessionLockRef.current = null
        frameQueueRef.current = []
        isStreamingActiveRef.current = false
        startFailureRef.current = { key: startKey, at: Date.now() }
        setStream(null)
        setError(message)
        setIsActive(false)
        setIsInitializing(false)
        setCurrentDeviceId(undefined)
        toast.error(message)
        logger.error('📷 [useCamera] Camera start failed:', err)
        return null
      } finally {
        startingRef.current = false
      }
    },
    [enumerateDevices, isInitializing, stopCamera]
  )

  const toggleCamera = useCallback(async () => {
    logger.debug('📷 [useCamera] toggleCamera called', {
      isActive,
      currentDeviceId,
      isInitializing,
    })
    if (isInitializing) {
      logger.debug('📷 [useCamera] Initialization in progress, ignoring toggle request.')
      return
    }
    if (isActive) {
      logger.debug('📷 [useCamera] Camera is active, stopping...')
      stopCamera()
    } else {
      logger.debug('📷 [useCamera] Camera is inactive, starting...')
      await startCamera(currentDeviceId)
    }
  }, [isActive, currentDeviceId, isInitializing, startCamera, stopCamera])

  const switchCamera = useCallback(async () => {
    if (!isActive) return
    if (availableDevices.length > 1) {
      const currentIndex = availableDevices.findIndex(device => device.deviceId === currentDeviceId)
      const nextIndex = (currentIndex + 1) % availableDevices.length
      const nextDevice = availableDevices[nextIndex]
      if (nextDevice?.deviceId) {
        await startCamera(nextDevice.deviceId)
      }
      return
    }
    const nextFacing: 'user' | 'environment' =
      facingModeRef.current === 'user' ? 'environment' : 'user'
    facingModeRef.current = nextFacing
    setFacingMode(nextFacing)
    await startCamera(undefined, nextFacing)
  }, [isActive, availableDevices, currentDeviceId, startCamera])

  const flipFacingMode = useCallback(async () => {
    const nextFacing: 'user' | 'environment' =
      facingModeRef.current === 'user' ? 'environment' : 'user'
    facingModeRef.current = nextFacing
    setFacingMode(nextFacing)
    await startCamera(undefined, nextFacing)
  }, [startCamera])

  const captureFrame = useCallback(
    async (videoElement?: HTMLVideoElement): Promise<CameraCapture | null> => {
      const video = videoElement || videoRef.current
      const canvas = canvasRef.current

      if (!video || !canvas) {
        metricsRef.current.failedCaptures++
        // DIAGNOSTIC: This is the most likely silent failure point — visible in production
        logger.warn('📷 [captureFrame] SKIPPED: missing video or canvas', {
          hasVideo: !!video,
          hasCanvas: !!canvas,
          hasVideoRef: !!videoRef.current,
          failedCaptures: metricsRef.current.failedCaptures,
        })
        return null
      }

      if (video.readyState < 2) {
        logger.warn('📷 [captureFrame] SKIPPED: video not ready', {
          readyState: video.readyState,
          failedCaptures: metricsRef.current.failedCaptures,
        })
        metricsRef.current.failedCaptures++
        return null
      }

      const startTime = performance.now()

      try {
        const frameResult = await captureVideoFrame(video, canvas, {
          maxDimension,
          quality: currentQualityRef.current,
        })

        if (!frameResult) {
          metricsRef.current.failedCaptures++
          return null
        }

        const { blob, imageData, width, height, timestamp } = frameResult

        // Validate frame quality synchronously from the capture canvas (no encode/decode roundtrip)
        const qualityScore = validateFrameQuality(canvas)
        const QUALITY_THRESHOLD = 0.4 // Minimum quality score to send frame

        // Add to frame buffer
        frameBufferRef.current.push({ imageData, quality: qualityScore, timestamp: Date.now() })
        if (frameBufferRef.current.length > MAX_BUFFER_SIZE) frameBufferRef.current.shift()

        recordCaptureTime(metricsRef.current, performance.now() - startTime)

        const capture: CameraCapture = {
          blob,
          imageData,
          timestamp,
          metadata: {
            width,
            height,
            ...(currentDeviceId ? { deviceId: currentDeviceId } : {}),
          },
        }

        onCapture?.(blob, imageData)
        setHasVerifiedFrame(true)

        // Log low-quality frames but ALWAYS continue to send.
        // The Gemini Live model can interpret imperfect frames; dropping them
        // causes gaps in the visual stream that are worse than blurry input.
        if (qualityScore < QUALITY_THRESHOLD) {
          logger.debug('📷 Frame quality is low, but sending anyway', {
            qualityScore,
            threshold: QUALITY_THRESHOLD,
          })
        }

        const now = Date.now()

        // Keep multimodal analysis running so the agent has fresh visual context
        // + tool snapshots instead of relying only on Gemini Live to interpret
        // streamed frames in real time.
        const shouldAnalyze =
          Boolean(sessionId) &&
          !hasLiveFrameTransport &&
          now - lastAnalysisAtRef.current >= ANALYSIS_INTERVAL_MS

        logger.debug('📹 [useCamera] captureFrame reached send path', {
          hasSendRealtimeInput: !!sendRealtimeInputRef.current,
          hasSendVideoFrame: !!sendVideoFrameRef.current,
          qualityScore,
          imageDataLength: imageData.length,
        })

        if (sendVideoFrameRef.current || sendRealtimeInputRef.current) {
          try {
            // Default to current frame; strip data URL prefix for raw base64
            let base64Data = imageData.split(',')[1] ?? imageData

            // If a significantly better frame is in the buffer, use it instead
            if (frameBufferRef.current.length > 1) {
              const bestFrame = frameBufferRef.current.reduce((best, current) =>
                current.quality > best.quality ? current : best
              )

              if (bestFrame.quality > qualityScore + 0.1) {
                base64Data = bestFrame.imageData.split(',')[1] ?? bestFrame.imageData
                logger.debug('📹 Using best quality frame from buffer', {
                  bufferSize: frameBufferRef.current.length,
                  bestQuality: bestFrame.quality,
                  currentQuality: qualityScore,
                })
              }
            }

            const bufferedAmount = getLiveTransportState?.().bufferedAmount ?? 0

            // Do not send when socket is already above max buffered threshold.
            // This prevents send-then-drop churn in the transport layer.
            if (bufferedAmount >= WEBSOCKET_CONFIG.MAX_BUFFERED_AMOUNT) {
              const now = Date.now()
              if (now - lastBackpressureWarnAtRef.current > 5000) {
                lastBackpressureWarnAtRef.current = now
                logger.debug('📹 Skipping webcam frame due to max WS buffer', {
                  bufferedAmount,
                  threshold: WEBSOCKET_CONFIG.MAX_BUFFERED_AMOUNT,
                })
              }
              return capture
            }

            // Graceful backpressure: compress frames before hitting max socket buffer.
            if (bufferedAmount > WEBSOCKET_CONFIG.HIGH_BUFFER_THRESHOLD) {
              currentQualityRef.current = WEBSOCKET_CONFIG.LOW_QUALITY_JPEG
            } else {
              currentQualityRef.current = quality
            }

            const frame: LiveMediaFrame = {
              mimeType: 'image/jpeg',
              data: base64Data,
            }

            // Send to Live API; respect backpressure drop from gateway
            let sent = false
            if (sendVideoFrameRef.current) {
              sent = sendVideoFrameRef.current(frame) !== false
            } else {
              sendRealtimeInputRef.current?.([frame])
              sent = true
            }
            if (!sent) {
              return capture
            }
            if (!hasSentFirstFrameRef.current) {
              logger.info('📹 [WEBCAM SENT] First frame sent to Live media transport', {
                size: base64Data.length,
                qualityScore,
                bufferedAmount,
                transport: sendVideoFrameRef.current ? 'sendVideoFrame' : 'sendRealtimeInput',
              })
              hasSentFirstFrameRef.current = true
            } else {
              logger.debug('📹 [WEBCAM SENT] Frame sent to Live media transport', {
                size: base64Data.length,
                qualityScore,
                bufferedAmount,
                transport: sendVideoFrameRef.current ? 'sendVideoFrame' : 'sendRealtimeInput',
              })
            }
          } catch (err) {
            logger.error('❌ Failed to stream webcam frame:', err)
          }
        }

        if (sessionId && shouldAnalyze) {
          await maybeAnalyzeAndBroadcast({
            blob,
            imageData,
            sessionId,
            modality: 'webcam',
            voiceConnectionId,
            lastAnalysisAtRef,
            analysisIntervalMs: ANALYSIS_INTERVAL_MS,
            onAnalysis,
            sendContextUpdate: update => {
              const previousAnalysis = metricsRef.current.lastAnalysis || undefined
              const delta = analyzeVisualDelta?.(update.analysis, previousAnalysis)
              metricsRef.current.lastAnalysis = update.analysis
              sendContextUpdate?.({
                ...update,
                metadata: {
                  ...(update.metadata || {}),
                  ...(delta
                    ? {
                        visualDeltaSignificant: delta.significant,
                        visualDeltaScore: delta.score,
                      }
                    : {}),
                },
              })
            },
            capturedAt: capture.timestamp,
            extraMetadata: {
              source: sendRealtimeInputRef.current ? 'webcam_stream' : 'webcam_capture',
              qualityScore,
              ...(voiceConnectionId ? { connectionId: voiceConnectionId } : {}),
            },
          })
        }

        logger.debug('📷 Camera capture:', {
          dimensions: `${capture.metadata?.width ?? 0}x${capture.metadata?.height ?? 0}`,
          blobSize: `${Math.round(capture.blob.size / 1024)}KB`,
          deviceId: capture.metadata?.deviceId ?? 'unknown',
          timestamp: capture.timestamp,
        })

        return capture
      } catch (err) {
        logger.error('Frame capture failed:', err)
        metricsRef.current.failedCaptures++
        return null
      }
    },
    [
      currentDeviceId,
      maxDimension,
      quality,
      onCapture,
      sessionId,
      voiceConnectionId,
      onAnalysis,
      sendContextUpdate,
      analyzeVisualDelta,
      getLiveTransportState,
    ]
  )

  useEffect(() => {
    captureFrameRef.current = captureFrame
  }, [captureFrame])

  const attachVideoElement = useCallback((element: HTMLVideoElement | null) => {
    videoRef.current = element

    if (!element) {
      return
    }

    logger.info('📷 [useCamera] Preview element attached', {
      hasStream: !!streamRef.current,
      streamId: streamRef.current?.id,
      readyState: element.readyState,
      currentTime: element.currentTime,
    })

    // If a stream already exists (e.g., remount after chat panel hide/show or
    // React Strict Mode double-mount), sync it immediately. The stream effect
    // only runs when `stream` state changes; it does not know about ref swaps.
    const currentStream = streamRef.current
    if (currentStream && element.srcObject !== currentStream) {
      element.srcObject = currentStream
      void element
        .play()
        .then(async () => {
          const readyVideo = await waitForVideoReady(() => videoRef.current, { timeoutMs: 8000 })
          if (readyVideo && enableAutoCapture && isActive) {
            void captureFrameRef.current?.()
          }
        })
        .catch(error => {
          logger.debug('📷 [useCamera] play() after remount stream sync failed', {
            error: error instanceof Error ? error.message : String(error),
          })
        })
    } else if (!currentStream && element.srcObject) {
      element.srcObject = null
    }
  }, [enableAutoCapture, isActive])

  // CANONICAL srcObject assignment path.
  // This is the ONLY place that assigns srcObject to a video element.
  // All other paths (startCamera, attachVideoElement, internal video creation)
  // just update `stream` state or `videoRef.current` and let this effect handle assignment.
  useEffect(() => {
    const element = videoRef.current
    if (!element) return
    if (stream && element.srcObject !== stream) {
      logger.info('📷 [useCamera] Syncing stream to video element (canonical path)', {
        hasStream: true,
        videoReadyState: element.readyState,
        streamId: stream.id,
      })
      element.srcObject = stream
      void element
        .play()
        .then(async () => {
          const readyVideo = await waitForVideoReady(() => videoRef.current, { timeoutMs: 8000 })
          if (readyVideo && enableAutoCapture && isActive) {
            void captureFrameRef.current?.()
          }
        })
        .catch(error => {
          logger.debug('📷 [useCamera] play() after stream sync failed', {
            error: error instanceof Error ? error.message : String(error),
          })
        })
    } else if (!stream && element.srcObject) {
      element.srcObject = null
    }
  }, [stream, enableAutoCapture, isActive])

  useEffect(() => {
    if (!sendVideoFrameRef.current && !sendRealtimeInputRef.current) return

    const processQueue = () => {
      if (frameQueueRef.current.length === 0) return

      const bufferedAmount = getLiveTransportState?.().bufferedAmount ?? 0

      if (bufferedAmount < WEBSOCKET_CONFIG.MAX_BUFFERED_AMOUNT) {
        const frame = frameQueueRef.current.shift()
        if (frame && (sendVideoFrameRef.current || sendRealtimeInputRef.current)) {
          try {
            if (sendVideoFrameRef.current) {
              sendVideoFrameRef.current(frame)
            } else {
              sendRealtimeInputRef.current?.([frame])
            }
            logger.debug('📹 Processed queued frame', {
              bufferedAmount,
              remainingQueue: frameQueueRef.current.length,
              transport: sendVideoFrameRef.current ? 'sendVideoFrame' : 'sendRealtimeInput',
            })
          } catch (err) {
            logger.error('❌ Failed to send queued frame:', err)
          }
        }
      }
    }

    queueProcessTimerRef.current = window.setInterval(
      processQueue,
      WEBSOCKET_CONFIG.FRAME_QUEUE_PROCESS_INTERVAL
    )

    return () => {
      if (queueProcessTimerRef.current) {
        clearInterval(queueProcessTimerRef.current)
        queueProcessTimerRef.current = null
      }
    }
  }, [getLiveTransportState, hasLiveFrameTransport])

  useEffect(() => {
    const shouldCapture =
      enableAutoCapture && isActive && (!requireVoiceSession || (sessionId && voiceConnectionId))

    // Log at debug level to reduce console spam on every re-render
    logger.debug('📷 [useCamera] Auto-capture evaluation', {
      shouldCapture,
      enableAutoCapture,
      isActive,
      requireVoiceSession,
      hasSessionId: !!sessionId,
      hasVoiceConnectionId: !!voiceConnectionId,
      hasLiveTransport: hasLiveFrameTransport,
      isLifecycleRunning: captureLifecycleRef.current?.isRunning ?? false,
      isStreaming: isStreamingActiveRef.current,
    })

    if (shouldCapture && captureLifecycleRef.current?.isRunning) {
      logger.debug('📷 [useCamera] Already capturing, skipping setup')
      return
    }

    if (!shouldCapture) {
      if (captureLifecycleRef.current?.isRunning) {
        logger.debug('📷 [useCamera] Stopping auto-capture (shouldCapture=false)')
        captureLifecycleRef.current.stop()
      }
      isStreamingActiveRef.current = false
      return
    }

    if (shouldCapture && hasLiveFrameTransport) {
      isStreamingActiveRef.current = true

      const STREAM_INTERVAL = RATE_LIMITS.WEBCAM_STREAM_INTERVAL
      logger.info(
        `📷 [useCamera] Starting webcam streaming at ${1000 / STREAM_INTERVAL} FPS (every ${STREAM_INTERVAL}ms)`
      )

      let cancelled = false

      void (async () => {
        const readyVideo = await waitForVideoReady(() => videoRef.current, { timeoutMs: 8000 })
        if (cancelled || !readyVideo) {
          if (!cancelled) {
            logger.warn('📷 [useCamera] Auto-capture aborted: video never became ready', {
              hasVideoRef: !!videoRef.current,
              readyState: videoRef.current?.readyState,
            })
          }
          return
        }

        captureLifecycleRef.current?.dispose()
        captureLifecycleRef.current = new CaptureLifecycle({
          label: '📷 [useCamera]',
          intervalMs: STREAM_INTERVAL,
          onTick: () => { void captureFrameRef.current?.() },
          logLevel: 'debug',
          fireImmediately: true,
        })
        captureLifecycleRef.current.start()
      })()

      return () => {
        cancelled = true
        captureLifecycleRef.current?.stop()
        isStreamingActiveRef.current = false
      }
    } else if (shouldCapture && !captureLifecycleRef.current?.isRunning && !isStreamingActiveRef.current) {
      logger.info(`📷 [useCamera] Starting auto-capture (non-streaming) every ${captureInterval}ms`)

      let cancelled = false

      void (async () => {
        const readyVideo = await waitForVideoReady(() => videoRef.current, { timeoutMs: 8000 })
        if (cancelled || !readyVideo) {
          if (!cancelled) {
            logger.warn('📷 [useCamera] Auto-capture aborted: video never became ready', {
              hasVideoRef: !!videoRef.current,
              readyState: videoRef.current?.readyState,
            })
          }
          return
        }

        captureLifecycleRef.current?.dispose()
        captureLifecycleRef.current = new CaptureLifecycle({
          label: '📷 [useCamera]',
          intervalMs: captureInterval,
          onTick: () => { void captureFrameRef.current?.() },
          logLevel: 'debug',
          fireImmediately: true,
        })
        captureLifecycleRef.current.start()
      })()

      return () => {
        cancelled = true
        captureLifecycleRef.current?.stop()
      }
    }

    return undefined
  }, [
    enableAutoCapture,
    isActive,
    captureInterval,
    hasLiveFrameTransport,
    requireVoiceSession,
    sessionId,
    voiceConnectionId,
    stream,
  ])

  useEffect(() => {
    return () => {
      stopCamera()
    }
  }, [stopCamera])

  const getMetrics = useCallback(() => getMetricsSnapshot(metricsRef.current), [])

  return {
    isActive,
    isInitializing,
    stream,
    error,
    videoRef,
    availableDevices,
    currentDeviceId,
    availableCameraCount: availableDevices.length,
    startCamera,
    stopCamera,
    toggleCamera,
    switchCamera,
    flipFacingMode,
    facingMode,
    captureFrame,
    attachVideoElement,
    hasVerifiedFrame,
    getMetrics,
  }
}
